"use client";

import { useEffect, useState, type ChangeEvent } from "react";
import { useLocation } from "wouter";
import { useOnboardingStore } from "../../lib/onboarding";
import * as Select from '@radix-ui/react-select';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { Input } from "@/components/ui/input";

const industryOptions = [
  "Technology",
  "Financial Services",
  "Healthcare",
  "Life Sciences",
  "Education",
  "Consulting",
  "Professional Services",
  "Manufacturing",
  "Retail & E-commerce",
  "Energy & Utilities",
  "Government & Public Sector",
  "Non-profit / NGO",
  "Media & Entertainment",
  "Hospitality & Travel",
  "Real Estate",
  "Consumer Packaged Goods",
  "Telecommunications",
  "Transportation & Logistics",
  "Sports & Gaming",
  "Other",
] as const;

type IndustryOption = (typeof industryOptions)[number];

const OnboardingStep4 = () => {
  const [, setLocation] = useLocation();
  const { industry, setIndustry } = useOnboardingStore();
  const [touched, setTouched] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<IndustryOption | "">("");
  const [customIndustry, setCustomIndustry] = useState("");

  useEffect(() => {
    if (!industry) {
      setSelectedIndustry((prev) => (prev === "Other" ? "Other" : ""));
      setCustomIndustry("");
      return;
    }

    if (industryOptions.includes(industry as IndustryOption)) {
      setSelectedIndustry(industry as IndustryOption);
      setCustomIndustry("");
      return;
    }

    setSelectedIndustry("Other");
    setCustomIndustry(industry ?? "");
  }, [industry]);

  const handleIndustrySelect = (value: IndustryOption) => {
    setSelectedIndustry(value);
    setTouched(false);

    if (value === "Other") {
      setIndustry(customIndustry);
      return;
    }

    setCustomIndustry("");
    setIndustry(value);
  };

  const handleCustomIndustryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCustomIndustry(value);
    setIndustry(value);
  };

  const handleNext = () => {
    setTouched(true);
    const isValidIndustry =
      selectedIndustry &&
      (selectedIndustry !== "Other" ? Boolean(industry) : Boolean(customIndustry.trim()));

    if (!isValidIndustry) {
      return;
    }
    if (selectedIndustry === "Other") {
      setIndustry(customIndustry.trim());
    }
    setLocation("/onboarding/step-5");
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-slate-900 font-display">Which industry are you operating in?</h2>
        <p className="mt-2 text-slate-600">Context on your industry helps us bring sharper, more relevant coaching examples.</p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="industry" className="block text-sm font-medium text-slate-700 font-sans">Primary Industry</label>
          <Select.Root value={selectedIndustry} onValueChange={handleIndustrySelect}>
            <Select.Trigger id="industry" className="mt-1 w-full flex justify-between items-center rounded-md border border-input bg-white px-4 py-2 text-slate-900 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
              <Select.Value placeholder="Select your industry..." />
              <Select.Icon><ChevronDownIcon className="h-5 w-5 opacity-50" /></Select.Icon>
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className="bg-white rounded-md shadow-lg border p-2 z-50 font-sans">
                <Select.Viewport>
                  {industryOptions.map((industryOption) => (
                    <Select.Item
                      key={industryOption}
                      value={industryOption}
                      className="relative flex items-center px-8 py-2 text-sm rounded-md cursor-pointer outline-none hover:bg-accent/10 focus:bg-accent/10"
                    >
                      <Select.ItemText>{industryOption}</Select.ItemText>
                      <Select.ItemIndicator className="absolute left-2 flex items-center text-primary"><CheckIcon className="h-5 w-5" /></Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>
        {selectedIndustry === "Other" && (
          <div className="space-y-2">
            <label htmlFor="custom-industry" className="block text-sm font-medium text-slate-700 font-sans">Tell us your industry</label>
            <Input
              id="custom-industry"
              value={customIndustry}
              onChange={handleCustomIndustryChange}
              placeholder="e.g. Climate Tech, Venture Capital, Space Systems"
            />
          </div>
        )}
        {touched && (!selectedIndustry || (selectedIndustry === "Other" && !customIndustry.trim())) && (
          <p className="text-sm text-red-600">Select the industry that best represents your current operating context.</p>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={() => setLocation("/onboarding/step-3")}
          className="inline-flex items-center justify-center px-6 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary font-display"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-base font-medium rounded-md shadow-sm bg-accent !text-accent-foreground hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent disabled:opacity-50 font-display"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default OnboardingStep4;
